// --- START OF FILE src/App.jsx ---

import React, { useState, useEffect, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { useTranslation } from 'react-i18next';
import { auth } from './firebase';
import LoginForm from './pages/LoginForm';
import MainScreen from './pages/MainScreen';
import ForgotPassword from './pages/ForgotPassword';
import { notificationManager } from './utils/NotificationManager';
import NotificationPrompt from './components/NotificationPrompt';

console.log('RESEYE App Component v1.0.3 loaded');

// Component para proteger rutas
// ... (rest of ProtectedRoute remains same)
function ProtectedRoute({ user, children }) {
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  return children;
}

// Componente Loader para la carga inicial de la aplicación
const AppLoader = () => (
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    backgroundColor: 'var(--bg-primary)',
    color: 'var(--text-primary)',
    fontFamily: '"Source Sans Pro", sans-serif'
  }}>
    <div className="loader-spinner"></div>
    <p style={{ marginTop: '20px', fontWeight: '500' }}>Iniciando ResEYE...</p>
  </div>
);

function App() {
  const { t } = useTranslation();
  const [usuario, setUsuario] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [promptConfig, setPromptConfig] = useState({ isOpen: false, type: 'enable' });

  useEffect(() => {
    const handleUnlock = () => {
      notificationManager.unlockAudio();
      // Remover listeners tras el primer desbloqueo
      window.removeEventListener('mousedown', handleUnlock);
      window.removeEventListener('keydown', handleUnlock);
      window.removeEventListener('touchstart', handleUnlock);
    };

    window.addEventListener('mousedown', handleUnlock);
    window.addEventListener('keydown', handleUnlock);
    window.addEventListener('touchstart', handleUnlock);

    const unsub = onAuthStateChanged(auth, async (user) => {
      setUsuario(user);
      setCargando(false);

      if (user) {
        // Initialize notifications and ensure Service Worker is registered
        notificationManager.registerServiceWorker().then(() => {
          // Si el permiso ya está dado, refrescamos el token para "curar" el vínculo
          if (Notification.permission === 'granted') {
            notificationManager.registerToken().catch(() => { });
          }
        });
        notificationManager.initForegroundListener();

        // Check for session conflict or need to activate
        const status = await notificationManager.checkSessionConflict(user.uid);
        if (status === 'conflict') {
          setPromptConfig({ isOpen: true, type: 'conflict' });
        } else if (status === 'inactive' && Notification.permission === 'default') {
          setPromptConfig({ isOpen: true, type: 'enable' });
        }
      }
    });
    return () => {
      unsub();
      window.removeEventListener('mousedown', handleUnlock);
      window.removeEventListener('keydown', handleUnlock);
      window.removeEventListener('touchstart', handleUnlock);
    };
  }, []);

  const handleLogout = async () => {
    // Current token cleanup before logout
    try {
      const { getToken, messaging } = await import('firebase/messaging');
      const { messaging: messagingInstance } = await import('./firebase');
      const token = await getToken(messagingInstance);
      if (token) await notificationManager.unregisterToken(token);
    } catch (e) {
      console.error('Error during token unregistration on logout:', e);
    }
    signOut(auth);
  };

  const handleConfirmPrompt = async () => {
    setPromptConfig(prev => ({ ...prev, isOpen: false }));
    await notificationManager.registerToken();
  };

  if (cargando) {
    return <AppLoader />;
  }

  return (
    <>
      <NotificationPrompt
        isOpen={promptConfig.isOpen}
        type={promptConfig.type}
        onConfirm={handleConfirmPrompt}
        onCancel={() => setPromptConfig(prev => ({ ...prev, isOpen: false }))}
        t={t}
      />
      <Routes>
        <Route
          path="/login"
          element={
            usuario ? <Navigate to="/" replace /> : <LoginForm onLoginSuccess={() => setUsuario(auth.currentUser)} />
          }
        />
        <Route
          path="/forgot-password"
          element={
            usuario ? <Navigate to="/" replace /> : <ForgotPassword />
          }
        />
        <Route
          path="/*"
          element={
            <ProtectedRoute user={usuario}>
              <MainScreen user={usuario} onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
      </Routes>
    </>
  );
}

// Envolvemos App con Suspense aquí también, por si App directamente necesita
// cargar traducciones antes de que sus hijos (lo cual es el caso para el loader).
// Si Suspense ya está en index.js envolviendo a App, esto podría ser redundante,
// pero no hace daño y asegura que App pueda suspender.
const AppWithSuspense = () => (
  <Suspense fallback={<AppLoader />}> {/* Usamos AppLoader como fallback de Suspense también */}
    <App />
  </Suspense>
);

export default AppWithSuspense; // MODIFICACIÓN: Exportar el componente envuelto
// --- END OF FILE src/App.jsx ---